import React, { useEffect, useRef } from "react";
import { Box, useTheme, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import WebAssetRoundedIcon from "@mui/icons-material/WebAssetRounded";
import ceLogoFull from "assets/ce-logo-full-h.png";
import ceLogo from "assets/ce-logo.svg";
import dayjs from "dayjs";
import QRCodeStyling from "qr-code-styling";

const LETTER_WIDTH = "21.6cm"; // US Letter width
const LETTER_HEIGHT = "27.9cm"; // US Letter height
const PAGE_PADDING = "1.5cm";
const ITEMS_PER_PAGE = 18; // Number of item rows that fit per page

// Helper function to calculate due date if not provided
const getCalculatedDueDate = (dueDate, dateOfIssue) => {
  if (dueDate && dueDate !== "MM-DD-YYYY") {
    return dueDate;
  }
  // If no due date, calculate as dateOfIssue + 30 days
  if (dateOfIssue && dateOfIssue !== "MM-DD-YYYY") {
    return dayjs(dateOfIssue).add(30, "day").format("MM-DD-YYYY");
  }
  return "MM-DD-YYYY";
};

// Helper function to create empty row objects
const createEmptyRows = (count) => {
  return Array.from({ length: count }, (_, i) => ({
    projectNumber: "",
    date: "",
    details: "",
    unit: "",
    rate: "",
    total: "",
    isEmpty: true,
  }));
};

// Helper function to extract custom heading and content from notes
const extractNotesHeadingAndContent = (notes) => {
  if (!notes) return { heading: "Notes:", content: "" };

  const lines = notes.split("\n");

  // Check if there's more than one line and first line ends with colon
  if (lines.length > 1 && lines[0].trim().endsWith(":")) {
    return {
      heading: lines[0].trim(),
      content: lines.slice(1).join("\n"),
    };
  }

  return {
    heading: "Notes:",
    content: notes,
  };
};

// QR Code component
const QRCodeComponent = ({ value, size = 200, theme }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!value || !canvasRef.current) return;
    const canvasElement = canvasRef.current;

    // Clear any existing content first
    canvasElement.innerHTML = "";

    const qr = new QRCodeStyling({
      width: size,
      height: size,
      data: value,
      image: ceLogo,
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.3,
        margin: 1,
        crossOrigin: "anonymous",
      },
      // === DOTS (main QR pattern) ===
      dotsOptions: {
        color: theme.palette.primary.main,
        // type options: "square" | "rounded" | "dots" | "classy" | "classy-rounded" | "extra-rounded"
        type: "rounded",
      },

      // === CORNERS SQUARE (3 big squares) ===
      cornersSquareOptions: {
        color: theme.palette.primary.main,
        // type options: "square" | "dot" | "extra-rounded"
        type: "extra-rounded",
      },

      // === CORNER DOTS (dots inside the 3 big squares) ===
      cornersDotOptions: {
        color: theme.palette.primary.main,
        // type options: "square" | "dot"
        type: "extra-rounded",
      },

      // === BACKGROUND ===
      backgroundOptions: {
        color: theme.palette.background.default,
      },
      margin: 0,
    });

    qr.append(canvasElement);

    return () => {
      canvasElement.innerHTML = "";
    };
  }, [value, size, theme]);

  if (!value) return null;

  return (
    <Box
      ref={canvasRef}
      sx={{
        width: size + "px",
        height: size + "px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    />
  );
};

// Component to render clickable project number
const ProjectNumberCell = ({ item, theme }) => {
  const navigate = useNavigate();

  const handleProjectClick = () => {
    // Don't navigate during print/PDF export
    if (window.matchMedia("print").matches) {
      return;
    }

    // Don't navigate for empty rows or if missing service info
    if (item.isEmpty || !item.serviceId || !item.serviceCategory) {
      return;
    }

    let routePath = "";
    let navData = { ComingFrom: "/invoices" };

    if (item.serviceCategory === "Translation") {
      routePath = `/translations/${item.serviceId}`;
      navData.TranslationID = item.serviceId;
    } else if (item.serviceCategory === "Interpretation") {
      routePath = `/interpretations/${item.serviceId}`;
      navData.InterpretationID = item.serviceId;
    } else if (item.serviceCategory === "Other") {
      routePath = `/otherServices/${item.serviceId}`;
      navData.OtherServiceID = item.serviceId;
    }

    if (routePath) {
      navigate(routePath, { state: navData });
    }
  };

  const isClickable = !item.isEmpty && item.serviceId && item.serviceCategory;

  return (
    <Typography
      variant="body3"
      onClick={handleProjectClick}
      sx={{
        cursor: isClickable ? "pointer" : "default",
        color: "inherit",
        textDecoration: "none",
        "&:hover": isClickable
          ? { opacity: 0.7, textDecoration: "underline" }
          : {},
        "@media print": {
          cursor: "default",
          color: "inherit",
          textDecoration: "none",
          "&:hover": {
            opacity: 1,
            textDecoration: "none",
          },
        },
      }}
    >
      {item.projectNumber}
    </Typography>
  );
};

const ClientNameText = ({ clientName, clientId, invoiceId }) => {
  const navigate = useNavigate();

  const handleClientClick = () => {
    if (window.matchMedia("print").matches) {
      return;
    }

    if (!clientId) {
      return;
    }

    const comingFrom = invoiceId ? `/invoices/${invoiceId}` : "/invoices";

    navigate(`/clients/${clientId}`, {
      state: {
        ComingFrom: comingFrom,
      },
    });
  };

  const isClickable = Boolean(clientId);

  return (
    <Typography
      variant="body3"
      component="span"
      onClick={handleClientClick}
      sx={{
        lineHeight: 1.1,
        display: "block",
        m: 0,
        cursor: isClickable ? "pointer" : "default",
        textDecoration: "none",
        color: "inherit",
        "&:hover": isClickable
          ? { opacity: 0.7, textDecoration: "underline" }
          : {},
        "@media print": {
          cursor: "default",
          color: "inherit",
          textDecoration: "none",
          "&:hover": {
            opacity: 1,
            textDecoration: "none",
          },
        },
      }}
    >
      {clientName}
    </Typography>
  );
};

const getRateDisplay = (item) => {
  if (!item) return "";
  if (typeof item.details === "string" && item.details.includes("Minimum")) {
    return "Minimum";
  }
  return item.rate;
};

const hasMatchingNextProjectNumber = (items, index) => {
  const currentItem = items?.[index];
  const nextItem = items?.[index + 1];

  if (!currentItem || !nextItem || currentItem.isEmpty || nextItem.isEmpty) {
    return false;
  }

  const currentProjectNumber = String(currentItem.projectNumber || "").trim();
  const nextProjectNumber = String(nextItem.projectNumber || "").trim();

  return !!currentProjectNumber && currentProjectNumber === nextProjectNumber;
};

const getItemRowDivider = (items, index, theme) => {
  const borderStyle = hasMatchingNextProjectNumber(items, index)
    ? "dashed"
    : "solid";

  return `1px ${borderStyle} ${theme.palette.divider}`;
};

const InvoiceItem = ({
  children,
  noBorderRadius = false,
  sx = {},
  ...rest
}) => {
  const theme = useTheme();

  return (
    <Box
      className="InvoiceItem"
      sx={{
        width: LETTER_WIDTH,
        minWidth: LETTER_WIDTH,
        maxWidth: LETTER_WIDTH,
        height: LETTER_HEIGHT,
        minHeight: LETTER_HEIGHT,
        maxHeight: LETTER_HEIGHT,
        bgcolor: theme.palette.background.paper,
        color: theme.palette.primary.text,
        borderRadius: noBorderRadius ? 0 : "8px",
        border: `0px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[6],
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

// Static page variants
const InvoicePageSingle = ({
  pagePadding = PAGE_PADDING,
  invoiceNumber = "0001",
  dateOfIssue = "MM-DD-YYYY",
  dueDate = "MM-DD-YYYY",
  paidDate = "MM-DD-YYYY",
  clientId = null,
  invoiceId = null,
  clientName = "Client Name",
  clientAddress = {
    line1: "Address Line 1",
    line2: "Address Line 2",
    zipCode: "ZIP Code",
  },
  purchaseOrders = "",
  items = [],
  subtotal = "$0.00",
  discount = "$0.00",
  total = "$0.00",
  notes = "",
  terms = "In the event of late payment, a 10% late fee will be added after 30 business days of receiving the invoice.",
  rowSpacing = "0.125cm",
  paymentLink = "",
}) => {
  const theme = useTheme();
  const calculatedDueDate = getCalculatedDueDate(dueDate, dateOfIssue);
  return (
    <InvoiceItem
      sx={{
        pt: pagePadding,
        pl: pagePadding,
        pr: pagePadding,
        pb: 0,
        gap: "0.5cm",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "10cm 8cm",
          columnGap: "0.6cm",
          alignItems: "start",
          maxWidth: "100%",
          mb: "0cm",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
            >
              Invoice
            </Typography>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {invoiceNumber}
            </Typography>
          </Box>
          <Box sx={{ my: "0.75rem" }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "0.25cm",
              mb: "0cm",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
            >
              Date of Issue
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {dateOfIssue}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
            >
              Due Date
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {calculatedDueDate}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            width: "100%",
            ml: "auto",
          }}
        >
          <Box
            component="img"
            src={ceLogoFull}
            alt="Communication Essentials Logo"
            sx={{
              justifySelf: "end",
              maxWidth: "7cm",
              maxHeight: "2.5cm",
              height: "auto",
              display: "block",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      {/* Top details */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "10.5cm 7.5cm",
          columnGap: "1cm",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            fontWeight={500}
            color={theme.palette.primary.main}
          >
            Billed To
          </Typography>
          <ClientNameText
            clientName={clientName}
            clientId={clientId}
            invoiceId={invoiceId}
          />
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            {clientAddress.line1}
          </Typography>
          {clientAddress.line2 && (
            <Typography
              variant="body3"
              component="span"
              sx={{ lineHeight: 1.1, display: "block", m: 0 }}
            >
              {clientAddress.line2}
            </Typography>
          )}
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            {clientAddress.zipCode}
          </Typography>

          {purchaseOrders && purchaseOrders.trim() !== "" && (
            <Box sx={{ mt: "0.35cm", display: "grid", rowGap: "0cm" }}>
              <Typography
                variant="h5"
                fontWeight={500}
                color={theme.palette.primary.main}
              >
                Purchase Order:
              </Typography>
              <Typography
                variant="body3"
                component="span"
                sx={{
                  lineHeight: 1.1,
                  display: "block",
                  m: 0,
                  whiteSpace: "pre-line",
                }}
              >
                {purchaseOrders}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "0cm",
          }}
        >
          <Typography
            variant="h5"
            fontWeight={500}
            color={theme.palette.primary.main}
            component="span"
            sx={{ lineHeight: 1.2, display: "block", m: 0 }}
          >
            From
            <br />
            Communication Essentials LLC
          </Typography>
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            PO Box 115
          </Typography>
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            Bausman, PA 17504-0115
          </Typography>
          <Typography
            variant="body3"
            component="a"
            href="tel:+17179922885"
            sx={{
              lineHeight: 1.1,
              display: "block",
              m: 0,
              textDecoration: "none",
              color: "inherit",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            (717) 992-2885
          </Typography>
          <Typography
            variant="body3"
            component="a"
            href={`mailto:amer@communicationessentialsllc.com?subject=Inquiry regarding Invoice ${invoiceNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              lineHeight: 1.1,
              display: "block",
              m: 0,
              textDecoration: "none",
              color: "inherit",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            amer@communicationessentialsllc.com
          </Typography>
        </Box>
      </Box>

      {/* Items */}
      <Box
        sx={{
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
            bgcolor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: "0.2cm",
            pt: "0.4cm",
            fontWeight: 700,
            columnGap: "0.2cm",
            maxWidth: "100%",
          }}
        >
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Project
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Date
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Details
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Unit
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Rate
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Total
            </Typography>
          </Box>
        </Box>

        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
              py: rowSpacing,
              borderBottom: getItemRowDivider(items, index, theme),
              columnGap: "0.2cm",
              maxWidth: "100%",
            }}
          >
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ProjectNumberCell item={item} theme={theme} />
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body3">{item.date}</Typography>
            </Box>
            <Box
              textAlign="start"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
              }}
            >
              <Typography
                variant="body3"
                component="span"
                sx={{
                  lineHeight: 1.2,
                  fontSize: 12,
                  display: "block",
                  textAlign: "start",
                }}
              >
                {item.details}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.unit}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body3">{getRateDisplay(item)}</Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.total}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* Empty rows to fill the page */}
        {createEmptyRows(Math.max(0, ITEMS_PER_PAGE - items.length)).map(
          (emptyItem, index) => (
            <Box
              key={`empty-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
                py: rowSpacing,
                //borderBottom: `1px solid ${theme.palette.divider}`,
                columnGap: "0.2cm",
                height: "0.8cm",
                maxWidth: "100%",
              }}
            >
              <Box textAlign="center">
                <ProjectNumberCell item={emptyItem} theme={theme} />
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.date}</Typography>
              </Box>
              <Box
                textAlign="center"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body3"
                  component="span"
                  sx={{
                    lineHeight: 1.2,
                    fontSize: 12,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  {emptyItem.details}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.unit}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.rate}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.total}</Typography>
              </Box>
            </Box>
          ),
        )}
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Bottom details */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1cm",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.3cm",
            width: "8cm",
          }}
        >
          {notes && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {(() => {
                const { heading, content } =
                  extractNotesHeadingAndContent(notes);
                return (
                  <>
                    <Typography
                      variant="h5"
                      fontWeight={500}
                      color={theme.palette.primary.main}
                    >
                      {heading}
                    </Typography>
                    <Typography
                      variant="body3"
                      component="span"
                      sx={{
                        lineHeight: 1.2,
                        fontSize: 12,
                        display: "block",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {content}
                    </Typography>
                  </>
                );
              })()}
            </Box>
          )}

          {paymentLink && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.2cm",
                mt: notes ? "0.3cm" : 0,
                width: "76px",
              }}
            >
              <Typography
                variant="body3"
                component="a"
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 10,
                  textAlign: "center",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  width: "100%",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Click here
              </Typography>
              <QRCodeComponent value={paymentLink} size={76} theme={theme} />
              <Typography
                variant="body3"
                sx={{
                  fontSize: 10,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                Or scan to pay
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            //p: "0.4cm",
            borderRadius: "6px",
            boxSizing: "border-box",
            width: "6cm",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
            }}
          >
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2" textAlign="right">
              {subtotal}
            </Typography>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2" textAlign="right">
              {discount}
            </Typography>
            <Typography
              mt="0.1cm"
              variant="body1"
              fontWeight={700}
              color={theme.palette.primary.main}
            >
              Total
            </Typography>
            <Typography
              mt="0.1cm"
              variant="body1"
              color={theme.palette.primary.main}
              fontWeight={700}
              textAlign="right"
            >
              {total}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1 }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: "0.2cm",
          width: "100%",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={500}
          color={theme.palette.primary.main}
        >
          Terms:
        </Typography>

        <Typography variant="body3" sx={{ whiteSpace: "pre-line" }}>
          {terms}
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            height: "1cm",
            width: "calc(100% + 3cm)",
            ml: "-1.5cm",
            mr: "-1.5cm",
            position: "relative",
            justifyContent: "flex-end",
            //gap: "0.5cm",
          }}
        >
          <Box
            sx={{
              width: "90%",
              bgcolor: "#333333",
              borderTopLeftRadius: "9999px",
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
              px: "0.5cm",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <EmailRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href={`mailto:amer@communicationessentialsllc.com?subject=Inquiry regarding Invoice ${invoiceNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                amer@communicationessentialsllc.com
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <WebAssetRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href="https://communicationessentialsllc.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                communicationessentialsllc.com
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <PhoneRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href="tel:+17179922885"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                (717) 992-2885
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            height: "1cm",
            width: "calc(100% + 3cm)",
            ml: "-1.5cm",
            mr: "-1.5cm",
            position: "relative",
          }}
        >
          {/* Background boxes - 50% each */}
          <Box
            sx={{
              position: "absolute",
              width: "50%",
              height: "100%",
              bgcolor: "#dcc7a9",
              left: 0,
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "50%",
              height: "100%",
              bgcolor: "#e8e6e1",
              right: 0,
              zIndex: 0,
            }}
          />

          {/* Colored boxes */}
          <Box
            sx={{
              width: "39.08%",
              bgcolor: theme.palette.primary.main,
              borderTopRightRadius: "9999px",
              position: "relative",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              width: "34.80%",
              bgcolor: "#dcc7a9",
              borderTopRightRadius: "9999px",
              position: "relative",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              width: "26.12%",
              bgcolor: "#e8e6e1",
              position: "relative",
              zIndex: 1,
            }}
          />
        </Box>
      </Box>
    </InvoiceItem>
  );
};

const InvoicePageFirst = ({
  pagePadding = PAGE_PADDING,
  invoiceNumber = "0001",
  dateOfIssue = "MM-DD-YYYY",
  dueDate = "MM-DD-YYYY",
  clientId = null,
  invoiceId = null,
  clientName = "Client Name",
  clientAddress = {
    line1: "Address Line 1",
    line2: "Address Line 2",
    zipCode: "ZIP Code",
  },
  purchaseOrders = "",
  items = [],
  pageNumber = 1,
  totalPages = 1,
  rowSpacing = "0.125cm",
}) => {
  const theme = useTheme();
  const calculatedDueDate = getCalculatedDueDate(dueDate, dateOfIssue);
  return (
    <InvoiceItem
      sx={{
        pt: pagePadding,
        pl: pagePadding,
        pr: pagePadding,
        pb: 0,
        gap: "0.5cm",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "10cm 8cm",
          columnGap: "0.6cm",
          alignItems: "start",
          maxWidth: "100%",
          mb: "0cm",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
            >
              Invoice
            </Typography>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {invoiceNumber}
            </Typography>
          </Box>
          <Box sx={{ my: "0.75rem" }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "0.25cm",
              mb: "0cm",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
            >
              Date of Issue
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {dateOfIssue}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
            >
              Due Date
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {calculatedDueDate}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            width: "100%",
            ml: "auto",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={ceLogoFull}
            alt="Communication Essentials Logo"
            sx={{
              justifySelf: "end",
              maxWidth: "7cm",
              maxHeight: "2.5cm",
              height: "auto",
              display: "block",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      {/* Top details */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "10.5cm 7.5cm",
          columnGap: "1cm",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            fontWeight={500}
            color={theme.palette.primary.main}
          >
            Billed To
          </Typography>
          <ClientNameText
            clientName={clientName}
            clientId={clientId}
            invoiceId={invoiceId}
          />
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            {clientAddress.line1}
          </Typography>
          {clientAddress.line2 && (
            <Typography
              variant="body3"
              component="span"
              sx={{ lineHeight: 1.1, display: "block", m: 0 }}
            >
              {clientAddress.line2}
            </Typography>
          )}
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            {clientAddress.zipCode}
          </Typography>

          {purchaseOrders && purchaseOrders.trim() !== "" && (
            <Box sx={{ mt: "0.35cm", display: "grid", rowGap: "0cm" }}>
              <Typography
                variant="h5"
                fontWeight={500}
                color={theme.palette.primary.main}
              >
                Purchase Order:
              </Typography>
              <Typography
                variant="body3"
                component="span"
                sx={{
                  lineHeight: 1.1,
                  display: "block",
                  m: 0,
                  whiteSpace: "pre-line",
                }}
              >
                {purchaseOrders}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "0cm",
          }}
        >
          <Typography
            variant="h5"
            fontWeight={500}
            color={theme.palette.primary.main}
            component="span"
            sx={{ lineHeight: 1.2, display: "block", m: 0 }}
          >
            From
            <br />
            Communication Essentials LLC
          </Typography>
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            PO Box 115
          </Typography>
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            Bausman, PA 17504-0115
          </Typography>
          <Typography
            variant="body3"
            component="span"
            sx={{ lineHeight: 1.1, display: "block", m: 0 }}
          >
            (717) 992-2885
          </Typography>
          <Typography
            variant="body3"
            component="a"
            href={`mailto:amer@communicationessentialsllc.com?subject=Inquiry regarding Invoice ${invoiceNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              lineHeight: 1.1,
              display: "block",
              m: 0,
              textDecoration: "none",
              color: "inherit",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            amer@communicationessentialsllc.com
          </Typography>
        </Box>
      </Box>

      {/* Items */}
      <Box
        sx={{
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
            bgcolor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: "0.2cm",
            pt: "0.4cm",
            fontWeight: 700,
            columnGap: "0.2cm",
            maxWidth: "100%",
          }}
        >
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Project
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Date
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Details
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Unit
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Rate
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Total
            </Typography>
          </Box>
        </Box>

        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
              py: rowSpacing,
              borderBottom: getItemRowDivider(items, index, theme),
              columnGap: "0.2cm",
              maxWidth: "100%",
            }}
          >
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ProjectNumberCell item={item} theme={theme} />
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.date}
              </Typography>
            </Box>
            <Box
              textAlign="start"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
              }}
            >
              <Typography
                variant="body3"
                component="span"
                sx={{
                  lineHeight: 1.2,
                  fontSize: 12,
                  display: "block",
                  textAlign: "start",
                }}
              >
                {item.details}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.unit}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {getRateDisplay(item)}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.total}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* Empty rows to fill the page */}
        {createEmptyRows(Math.max(0, ITEMS_PER_PAGE - items.length)).map(
          (emptyItem, index) => (
            <Box
              key={`empty-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
                py: rowSpacing,
                //borderBottom: `1px solid ${theme.palette.divider}`,
                columnGap: "0.2cm",
                height: "0.8cm",
                maxWidth: "100%",
              }}
            >
              <Box textAlign="center">
                <ProjectNumberCell item={emptyItem} theme={theme} />
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.date}</Typography>
              </Box>
              <Box
                textAlign="center"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body3"
                  component="span"
                  sx={{
                    lineHeight: 1.2,
                    fontSize: 12,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  {emptyItem.details}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.unit}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.rate}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.total}</Typography>
              </Box>
            </Box>
          ),
        )}
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Footer (no bottom details) */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            height: "1cm",
            width: "calc(100% + 3cm)",
            ml: "-1.5cm",
            mr: "-1.5cm",
            position: "relative",
            justifyContent: "flex-end",
            //gap: "0.5cm",
          }}
        >
          <Box
            sx={{
              width: "10%",
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {pageNumber}/{totalPages}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "90%",
              bgcolor: "#333333",
              borderTopLeftRadius: "9999px",
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
              px: "0.5cm",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <EmailRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href={`mailto:amer@communicationessentialsllc.com?subject=Inquiry regarding Invoice ${invoiceNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                amer@communicationessentialsllc.com
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <WebAssetRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href="https://communicationessentialsllc.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                communicationessentialsllc.com
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <PhoneRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href="tel:+17179922885"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                (717) 992-2885
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            height: "1cm",
            width: "calc(100% + 3cm)",
            ml: "-1.5cm",
            mr: "-1.5cm",
            position: "relative",
          }}
        >
          {/* Background boxes - 50% each */}
          <Box
            sx={{
              position: "absolute",
              width: "50%",
              height: "100%",
              bgcolor: "#dcc7a9",
              left: 0,
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "50%",
              height: "100%",
              bgcolor: "#e8e6e1",
              right: 0,
              zIndex: 0,
            }}
          />

          {/* Colored boxes */}
          <Box
            sx={{
              width: "39.08%",
              bgcolor: theme.palette.primary.main,
              borderTopRightRadius: "9999px",
              position: "relative",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              width: "34.80%",
              bgcolor: "#dcc7a9",
              borderTopRightRadius: "9999px",
              position: "relative",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              width: "26.12%",
              bgcolor: "#e8e6e1",
              position: "relative",
              zIndex: 1,
            }}
          />
        </Box>
      </Box>
    </InvoiceItem>
  );
};

const InvoicePageMiddle = ({
  pagePadding = PAGE_PADDING,
  invoiceNumber = "0001",
  dateOfIssue = "MM-DD-YYYY",
  dueDate = "MM-DD-YYYY",
  items = [],
  pageNumber = 2,
  totalPages = 2,
  rowSpacing = "0.125cm",
}) => {
  const theme = useTheme();
  const calculatedDueDate = getCalculatedDueDate(dueDate, dateOfIssue);
  return (
    <InvoiceItem
      sx={{
        pt: pagePadding,
        pl: pagePadding,
        pr: pagePadding,
        pb: 0,
        gap: "0.5cm",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "10cm 8cm",
          columnGap: "0.6cm",
          alignItems: "start",
          maxWidth: "100%",
          mb: "0cm",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
            >
              Invoice
            </Typography>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {invoiceNumber}
            </Typography>
          </Box>
          <Box sx={{ my: "0.75rem" }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "0.25cm",
              mb: "0cm",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
            >
              Date of Issue
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {dateOfIssue}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
            >
              Due Date
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {calculatedDueDate}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            width: "100%",
            ml: "auto",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={ceLogoFull}
            alt="Communication Essentials Logo"
            sx={{
              justifySelf: "end",
              maxWidth: "7cm",
              maxHeight: "2.5cm",
              height: "auto",
              display: "block",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      <Box sx={{ height: "1cm" }} />

      {/* Items only */}
      <Box
        sx={{
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
            bgcolor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: "0.2cm",
            pt: "0.4cm",
            fontWeight: 700,
            columnGap: "0.2cm",
            maxWidth: "100%",
          }}
        >
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Project
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Date
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Details
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Unit
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Rate
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Total
            </Typography>
          </Box>
        </Box>

        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
              py: rowSpacing,
              borderBottom: getItemRowDivider(items, index, theme),
              columnGap: "0.2cm",
              maxWidth: "100%",
            }}
          >
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ProjectNumberCell item={item} theme={theme} />
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.date}
              </Typography>
            </Box>
            <Box
              textAlign="start"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
              }}
            >
              <Typography
                variant="body3"
                component="span"
                sx={{
                  lineHeight: 1.2,
                  fontSize: 12,
                  display: "block",
                  textAlign: "start",
                }}
              >
                {item.details}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.unit}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {getRateDisplay(item)}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.total}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* Empty rows to fill the page */}
        {createEmptyRows(Math.max(0, ITEMS_PER_PAGE - items.length)).map(
          (emptyItem, index) => (
            <Box
              key={`empty-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
                py: rowSpacing,
                //borderBottom: `1px solid ${theme.palette.divider}`,
                columnGap: "0.2cm",
                height: "0.8cm",
                maxWidth: "100%",
              }}
            >
              <Box textAlign="center">
                <ProjectNumberCell item={emptyItem} theme={theme} />
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.date}</Typography>
              </Box>
              <Box
                textAlign="center"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body3"
                  component="span"
                  sx={{
                    lineHeight: 1.2,
                    fontSize: 12,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  {emptyItem.details}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.unit}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.rate}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.total}</Typography>
              </Box>
            </Box>
          ),
        )}
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Footer only */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            height: "1cm",
            width: "calc(100% + 3cm)",
            ml: "-1.5cm",
            mr: "-1.5cm",
            position: "relative",
            justifyContent: "flex-end",
            //gap: "0.5cm",
          }}
        >
          <Box
            sx={{
              width: "10%",
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {pageNumber}/{totalPages}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "90%",
              bgcolor: "#333333",
              borderTopLeftRadius: "9999px",
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
              px: "0.5cm",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <EmailRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href={`mailto:amer@communicationessentialsllc.com?subject=Inquiry regarding Invoice ${invoiceNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                amer@communicationessentialsllc.com
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <WebAssetRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href="https://communicationessentialsllc.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                communicationessentialsllc.com
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <PhoneRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href="tel:+17179922885"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                (717) 992-2885
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            height: "1cm",
            width: "calc(100% + 3cm)",
            ml: "-1.5cm",
            mr: "-1.5cm",
            position: "relative",
          }}
        >
          {/* Background boxes - 50% each */}
          <Box
            sx={{
              position: "absolute",
              width: "50%",
              height: "100%",
              bgcolor: "#dcc7a9",
              left: 0,
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "50%",
              height: "100%",
              bgcolor: "#e8e6e1",
              right: 0,
              zIndex: 0,
            }}
          />

          {/* Colored boxes */}
          <Box
            sx={{
              width: "39.08%",
              bgcolor: theme.palette.primary.main,
              borderTopRightRadius: "9999px",
              position: "relative",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              width: "34.80%",
              bgcolor: "#dcc7a9",
              borderTopRightRadius: "9999px",
              position: "relative",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              width: "26.12%",
              bgcolor: "#e8e6e1",
              position: "relative",
              zIndex: 1,
            }}
          />
        </Box>
      </Box>
    </InvoiceItem>
  );
};

const InvoicePageLast = ({
  pagePadding = PAGE_PADDING,
  invoiceNumber = "0001",
  dateOfIssue = "MM-DD-YYYY",
  dueDate = "MM-DD-YYYY",
  items = [],
  subtotal = "$0.00",
  discount = "$0.00",
  total = "$0.00",
  notes = "",
  terms = "In the event of late payment, a 10% late fee will be added after 30 business days of receiving the invoice.",
  pageNumber = 2,
  totalPages = 2,
  rowSpacing = "0.125cm",
  paymentLink = "",
}) => {
  const theme = useTheme();
  const calculatedDueDate = getCalculatedDueDate(dueDate, dateOfIssue);
  return (
    <InvoiceItem
      sx={{
        pt: pagePadding,
        pl: pagePadding,
        pr: pagePadding,
        pb: 0,
        gap: "0.5cm",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "10cm 8cm",
          columnGap: "0.6cm",
          alignItems: "start",
          maxWidth: "100%",
          mb: "0cm",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
            >
              Invoice
            </Typography>
            <Typography
              variant="h2"
              fontWeight={500}
              gutterBottom
              color={theme.palette.primary.main}
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {invoiceNumber}
            </Typography>
          </Box>
          <Box sx={{ my: "0.75rem" }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "0.25cm",
              mb: "0cm",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
            >
              Date of Issue
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {dateOfIssue}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.25cm" }}>
            <Typography
              variant="h5"
              fontWeight={500}
              color={theme.palette.primary.main}
            >
              Due Date
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontFamily: ["Work Sans", "sans-serif"].join(",") }}
            >
              {calculatedDueDate}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            width: "100%",
            ml: "auto",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={ceLogoFull}
            alt="Communication Essentials Logo"
            sx={{
              justifySelf: "end",
              maxWidth: "7cm",
              maxHeight: "2.5cm",
              height: "auto",
              display: "block",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      <Box sx={{ height: "1cm" }} />

      {/* Items */}
      <Box
        sx={{
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
            bgcolor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: "0.2cm",
            pt: "0.4cm",
            fontWeight: 700,
            columnGap: "0.2cm",
            maxWidth: "100%",
          }}
        >
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Project
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Date
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Details
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Unit
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Rate
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color={theme.palette.primary.main}>
              Total
            </Typography>
          </Box>
        </Box>

        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
              py: rowSpacing,
              borderBottom: getItemRowDivider(items, index, theme),
              columnGap: "0.2cm",
              maxWidth: "100%",
            }}
          >
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ProjectNumberCell item={item} theme={theme} />
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.date}
              </Typography>
            </Box>
            <Box
              textAlign="start"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
              }}
            >
              <Typography
                variant="body3"
                component="span"
                sx={{
                  lineHeight: 1.2,
                  fontSize: 12,
                  display: "block",
                  textAlign: "start",
                }}
              >
                {item.details}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.unit}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.rate}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography textAlign="center" variant="body3">
                {item.total}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* Empty rows to fill the page */}
        {createEmptyRows(Math.max(0, ITEMS_PER_PAGE - items.length)).map(
          (emptyItem, index) => (
            <Box
              key={`empty-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "2cm 2cm 8cm 2.5cm 1.5cm 1.5cm",
                py: rowSpacing,
                //borderBottom: `1px solid ${theme.palette.divider}`,
                columnGap: "0.2cm",
                height: "0.8cm",
                maxWidth: "100%",
              }}
            >
              <Box textAlign="center">
                <ProjectNumberCell item={emptyItem} theme={theme} />
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.date}</Typography>
              </Box>
              <Box
                textAlign="center"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body3"
                  component="span"
                  sx={{
                    lineHeight: 1.2,
                    fontSize: 12,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  {emptyItem.details}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.unit}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.rate}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body3">{emptyItem.total}</Typography>
              </Box>
            </Box>
          ),
        )}
      </Box>

      <Box sx={{ height: "0.5cm" }} />

      {/* Bottom details only */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1cm",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.3cm",
            width: "8cm",
          }}
        >
          {notes && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {(() => {
                const { heading, content } =
                  extractNotesHeadingAndContent(notes);
                return (
                  <>
                    <Typography
                      variant="h5"
                      fontWeight={500}
                      color={theme.palette.primary.main}
                    >
                      {heading}
                    </Typography>
                    <Typography
                      variant="body3"
                      component="span"
                      sx={{
                        lineHeight: 1.2,
                        fontSize: 12,
                        display: "block",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {content}
                    </Typography>
                  </>
                );
              })()}
            </Box>
          )}
          {paymentLink && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.2cm",
                mt: notes ? "0.3cm" : 0,
                width: "76px",
              }}
            >
              <Typography
                variant="body3"
                component="a"
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 10,
                  textAlign: "center",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  width: "100%",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Click here
              </Typography>
              <QRCodeComponent value={paymentLink} size={76} theme={theme} />
              <Typography
                variant="body3"
                sx={{
                  fontSize: 10,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                Or scan to pay
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            //p: "0.4cm",
            borderRadius: "6px",
            boxSizing: "border-box",
            width: "6cm",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
            }}
          >
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2" textAlign="right">
              {subtotal}
            </Typography>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2" textAlign="right">
              {discount}
            </Typography>
            <Typography
              mt="0.1cm"
              variant="body1"
              fontWeight={700}
              color={theme.palette.primary.main}
            >
              Total
            </Typography>
            <Typography
              mt="0.1cm"
              variant="body1"
              color={theme.palette.primary.main}
              fontWeight={700}
              textAlign="right"
            >
              {total}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1 }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: "0.2cm",
          width: "100%",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={500}
          color={theme.palette.primary.main}
        >
          Terms:
        </Typography>

        <Typography variant="body3" sx={{ whiteSpace: "pre-line" }}>
          {terms}
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            height: "1cm",
            width: "calc(100% + 3cm)",
            ml: "-1.5cm",
            mr: "-1.5cm",
            position: "relative",
            justifyContent: "flex-end",
            //gap: "0.5cm",
          }}
        >
          <Box
            sx={{
              width: "10%",
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {pageNumber}/{totalPages}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "93%",
              bgcolor: "#333333",
              borderTopLeftRadius: "9999px",
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
              px: "0.5cm",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <EmailRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href={`mailto:amer@communicationessentialsllc.com?subject=Inquiry regarding Invoice ${invoiceNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                amer@communicationessentialsllc.com
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <WebAssetRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                component="a"
                href="https://communicationessentialsllc.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.background.default,
                  fontSize: 13,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                communicationessentialsllc.com
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.2cm",
              }}
            >
              <PhoneRoundedIcon
                sx={{
                  fontSize: "0.5cm",
                  color: theme.palette.background.default,
                }}
              />
              <Typography
                variant="body1"
                sx={{ color: theme.palette.background.default, fontSize: 13 }}
              >
                (717) 992-2885
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            height: "1cm",
            width: "calc(100% + 3cm)",
            ml: "-1.5cm",
            mr: "-1.5cm",
            position: "relative",
          }}
        >
          {/* Background boxes - 50% each */}
          <Box
            sx={{
              position: "absolute",
              width: "50%",
              height: "100%",
              bgcolor: "#dcc7a9",
              left: 0,
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "50%",
              height: "100%",
              bgcolor: "#e8e6e1",
              right: 0,
              zIndex: 0,
            }}
          />

          {/* Colored boxes */}
          <Box
            sx={{
              width: "39.08%",
              bgcolor: theme.palette.primary.main,
              borderTopRightRadius: "9999px",
              position: "relative",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              width: "34.80%",
              bgcolor: "#dcc7a9",
              borderTopRightRadius: "9999px",
              position: "relative",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              width: "26.12%",
              bgcolor: "#e8e6e1",
              position: "relative",
              zIndex: 1,
            }}
          />
        </Box>
      </Box>
    </InvoiceItem>
  );
};

const Invoice = React.forwardRef((props, ref) => {
  const {
    items = [],
    itemsPerFirstPage = 11,
    itemsPerPage = 11,
    rowSpacing = "0.09cm",
    noBorderRadius = false,
    ...rest
  } = props;
  const totalItems = items.length;

  if (totalItems <= itemsPerFirstPage) {
    return (
      <Box
        ref={ref}
        sx={{ display: "flex", flexDirection: "column", gap: "1cm" }}
      >
        <InvoicePageSingle
          {...rest}
          items={items}
          rowSpacing={rowSpacing}
          noBorderRadius={noBorderRadius}
        />
      </Box>
    );
  }

  const firstItems = items.slice(0, itemsPerFirstPage);
  const remainingItems = items.slice(itemsPerFirstPage);

  if (remainingItems.length <= itemsPerPage) {
    const totalPages = 2;
    return (
      <Box
        ref={ref}
        sx={{ display: "flex", flexDirection: "column", gap: "1cm" }}
      >
        <InvoicePageFirst
          {...rest}
          items={firstItems}
          pageNumber={1}
          totalPages={totalPages}
          rowSpacing={rowSpacing}
          noBorderRadius={noBorderRadius}
        />
        <InvoicePageLast
          {...rest}
          items={remainingItems}
          pageNumber={2}
          totalPages={totalPages}
          rowSpacing={rowSpacing}
          noBorderRadius={noBorderRadius}
        />
      </Box>
    );
  }

  const additionalPages = [];
  let leftover = [...remainingItems];

  while (leftover.length > itemsPerPage) {
    additionalPages.push(leftover.slice(0, itemsPerPage));
    leftover = leftover.slice(itemsPerPage);
  }

  const lastItems = leftover;
  const totalPages = 1 + additionalPages.length + 1; // first + additional pages + last

  return (
    <Box
      ref={ref}
      sx={{ display: "flex", flexDirection: "column", gap: "1cm" }}
    >
      <InvoicePageFirst
        {...rest}
        items={firstItems}
        pageNumber={1}
        totalPages={totalPages}
        rowSpacing={rowSpacing}
        noBorderRadius={noBorderRadius}
      />
      {additionalPages.map((chunk, index) => (
        <InvoicePageMiddle
          key={`middle-${index}`}
          {...rest}
          items={chunk}
          pageNumber={index + 2}
          totalPages={totalPages}
          rowSpacing={rowSpacing}
          noBorderRadius={noBorderRadius}
        />
      ))}
      <InvoicePageLast
        {...rest}
        items={lastItems}
        pageNumber={totalPages}
        totalPages={totalPages}
        rowSpacing={rowSpacing}
        noBorderRadius={noBorderRadius}
      />
    </Box>
  );
});

export default InvoiceItem;
export {
  Invoice,
  InvoicePageSingle,
  InvoicePageFirst,
  InvoicePageMiddle,
  InvoicePageLast,
};
